import { AppConfig } from '@/config/app.config';
import useMediaQuery from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import React, { useState } from 'react';

const PrivacyPolicy = () => {
	const [activeSection, setActiveSection] = useState<string | null>(null);

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);

	const sections = [
		{ id: 'introduction', title: 'Introduction' },
		{
			id: 'interpretation-definitions',
			title: 'Interpretation and Definitions',
		},
		{
			id: 'collecting-using-data',
			title: 'Collecting and Using Your Personal Data',
		},
		{ id: 'tracking-cookies', title: 'Tracking Technologies and Cookies' },
		{ id: 'use-of-data', title: 'Use of Your Personal Data' },
		{ id: 'retention-of-data', title: 'Retention of Your Personal Data' },
		{ id: 'transfer-of-data', title: 'Transfer of Your Personal Data' },
		{ id: 'delete-data', title: 'Delete Your Personal Data' },
		{ id: 'disclosure-of-data', title: 'Disclosure of Your Personal Data' },
		{ id: 'security-of-data', title: 'Security of Your Personal Data' },
		{ id: 'childrens-privacy', title: "Children's Privacy" },
		{ id: 'links-to-other-websites', title: 'Links to Other Websites' },
		{ id: 'changes-to-policy', title: 'Changes to this Privacy Policy' },
		{ id: 'contact-us', title: 'Contact Us' },
	];

	return (
		<div className="max-w-screen bg-gray-50 min-h-screen bg-[url('/img/background.png')] py-4 px-4 sm:px-6 lg:px-8">
			<div className='max-w-5xl mx-auto bg-white shadow-toast rounded-lg overflow-hidden border border-popover-100'>
				{/* Header */}
				<div className='bg-gradient-to-r from-popover-1000 to-popover-800 px-6 py-4'>
					<h1 className='text-3xl font-bold text-white'>
						Privacy Policy
					</h1>
					<p className='text-blue-100 mt-1'>
						Last updated: May 16, 2025
					</p>
				</div>

				{/* Navigation */}
				<nav className='sticky top-0 bg-white z-10 border-b border-gray-200 px-6 py-3'>
					<h2 className='text-lg font-semibold text-gray-800 mb-2'>
						Table of Contents
					</h2>
					<ul className='flex flex-wrap gap-3'>
						{sections.map((section) => (
							<li key={section.id}>
								<a
									href={`#${section.id}`}
									className={`text-sm text-blue-600 hover:underline ${
										activeSection === section.id
											? 'font-semibold'
											: ''
									}`}
									onClick={() =>
										setActiveSection(section.id)
									}>
									{section.title}
								</a>
							</li>
						))}
					</ul>
				</nav>

				{/* Content Container */}
				<div className='p-6 sm:p-8'>
					{/* Interpretation and Definitions */}
					<section
						id='interpretation-definitions'
						className='mb-8'>
						<h2 className='text-2xl font-semibold text-gray-800 mb-4'>
							Interpretation and Definitions
						</h2>

						<h3 className='text-xl font-medium text-gray-700 mb-2'>
							Interpretation
						</h3>
						<p className='text-gray-600 mb-4'>
							The words of which the initial letter is capitalized
							have meanings defined under the following
							conditions. The following definitions shall have the
							same meaning regardless of whether they appear in
							singular or in plural.
						</p>

						<h3 className='text-xl font-medium text-gray-700 mb-2'>
							Definitions
						</h3>
						<p className='text-gray-600 mb-2'>
							For the purposes of this Privacy Policy:
						</p>

						<ul className='list-disc pl-5 space-y-2 text-gray-600'>
							<li>
								<strong>Account</strong> means a unique account
								created for You to access our Service or parts
								of our Service.
							</li>
							<li>
								<strong>Affiliate</strong> means an entity that
								controls, is controlled by, or is under common
								control with a party, where "control" means
								ownership of 50% or more of the shares, equity
								interest, or other securities entitled to vote
								for election of directors or other managing
								authority.
							</li>
							<li>
								<strong>Company</strong> (referred to as either
								"the Company", "We", "Us", or "Our" in this
								Agreement) refers to OwnInn.
							</li>
							<li>
								<strong>Cookies</strong> are small files that
								are placed on Your computer, mobile device, or
								any other device by a website, containing the
								details of Your browsing history on that website
								among its many uses.
							</li>
							<li>
								<strong>Country</strong> refers to: Georgia
							</li>
							<li>
								<strong>Device</strong> means any device that
								can access the Service such as a computer, a
								cellphone, or a digital tablet.
							</li>
							<li>
								<strong>Personal Data</strong> is any
								information that relates to an identified or
								identifiable individual.
							</li>
							<li>
								<strong>Service</strong> refers to the Website.
							</li>
							<li>
								<strong>Service Provider</strong> means any
								natural or legal person who processes the data
								on behalf of the Company. It refers to
								third-party companies or individuals employed by
								the Company to facilitate the Service, to
								provide the Service on behalf of the Company, to
								perform services related to the Service, or to
								assist the Company in analyzing how the Service
								is used.
							</li>
							<li>
								<strong>Usage Data</strong> refers to data
								collected automatically, either generated by the
								use of the Service or from the Service
								infrastructure itself (for example, the duration
								of a page visit).
							</li>
							<li>
								<strong>Website</strong> refers to OwnInn,
								accessible from{' '}
								<a
									href='https://app.owninn.ge'
									target='_blank'
									rel='noopener noreferrer'
									className='text-blue-600 hover:underline flex items-center'>
									https://app.owninn.ge
									<svg
										className='w-4 h-4 ml-1'
										fill='none'
										stroke='currentColor'
										viewBox='0 0 24 24'
										xmlns='http://www.w3.org/2000/svg'>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											strokeWidth='2'
											d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
										/>
									</svg>
								</a>
							</li>
							<li>
								<strong>You</strong> means the individual
								accessing or using the Service, or the company,
								or other legal entity on behalf of which such
								individual is accessing or using the Service, as
								applicable.
							</li>
						</ul>
					</section>

					{/* Collecting and Using Personal Data */}
					<section
						id='collecting-using-data'
						className='mb-8'>
						<h2 className='text-2xl font-semibold text-gray-800 mb-4'>
							Collecting and Using Your Personal Data
						</h2>

						<h3 className='text-xl font-medium text-gray-700 mb-2'>
							Types of Data Collected
						</h3>

						<h4 className='text-lg font-medium text-gray-700 mb-1'>
							Personal Data
						</h4>
						<p className='text-gray-600 mb-4'>
							While using Our Service, We may ask You to provide
							Us with certain personally identifiable information
							that can be used to contact or identify You.
							Personally identifiable information may include, but
							is not limited to:
						</p>
						<ul className='list-disc pl-5 space-y-2 text-gray-600 mb-6'>
							<li>Email address</li>
							<li>First name and last name</li>
							<li>Phone number</li>
							<li>
								Address, State, Province, ZIP/Postal code, City
							</li>
							<li>Usage Data</li>
						</ul>

						<h4 className='text-lg font-medium text-gray-700 mb-1'>
							Usage Data
						</h4>
						<p className='text-gray-600 mb-4'>
							Usage Data is collected automatically when using the
							Service.
						</p>
						<p className='text-gray-600 mb-4'>
							Usage Data may include information such as Your
							Device's Internet Protocol address (e.g. IP
							address), browser type, browser version, the pages
							of our Service that You visit, the time and date of
							Your visit, the time spent on those pages, unique
							device identifiers, and other diagnostic data.
						</p>
						<p className='text-gray-600'>
							When You access the Service by or through a mobile
							device, We may collect certain information
							automatically, including, but not limited to, the
							type of mobile device You use, Your mobile device
							unique ID, the IP address of Your mobile device,
							Your mobile operating system, the type of mobile
							Internet browser You use, unique device identifiers,
							and other diagnostic data.
						</p>
					</section>

					{/* Tracking Technologies and Cookies */}
					<section
						id='tracking-cookies'
						className='mb-8'>
						<h2 className='text-2xl font-semibold text-gray-800 mb-4'>
							Tracking Technologies and Cookies
						</h2>
						<p className='text-gray-600 mb-4'>
							We use Cookies and similar tracking technologies to
							track the activity on Our Service and store certain
							information. Tracking technologies used are beacons,
							tags, and scripts to collect and track information
							and to improve and analyze Our Service. The
							technologies We use may include:
						</p>

						<ul className='list-disc pl-5 space-y-2 text-gray-600 mb-4'>
							<li>
								<strong>Cookies or Browser Cookies.</strong> A
								cookie is a small file placed on Your Device.
								You can instruct Your browser to refuse all
								Cookies or to indicate when a Cookie is being
								sent. However, if You do not accept Cookies, You
								may not be able to use some parts of our
								Service.
							</li>
							<li>
								<strong>Web Beacons.</strong> Certain sections
								of our Service and our emails may contain small
								electronic files known as web beacons that
								permit the Company, for example, to count users
								who have visited those pages or opened an email.
							</li>
						</ul>

						<p className='text-gray-600 mb-4'>
							Cookies can be "Persistent" or "Session" Cookies.
							Persistent Cookies remain on Your device when You go
							offline, while Session Cookies are deleted as soon
							as You close Your web browser. Learn more about
							cookies on the{' '}
							<a
								href='https://www.freeprivacypolicy.com/blog/sample-privacy-policy-template/'
								target='_blank'
								rel='noopener noreferrer'
								className='text-blue-600 hover:underline flex items-center'>
								Free Privacy Policy website article
								<svg
									className='w-4 h-4 ml-1'
									fill='none'
									stroke='currentColor'
									viewBox='0 0 24 24'
									xmlns='http://www.w3.org/2000/svg'>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth='2'
										d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
									/>
								</svg>
							</a>
						</p>

						<p className='text-gray-600 mb-2'>
							We use both Session and Persistent Cookies for the
							purposes set out below:
						</p>

						<div className='overflow-x-auto mb-4'>
							<table
								className={cn(
									'min-w-full border border-gray-200 rounded-lg',
									isMobile && 'border-0',
								)}>
								<thead className='bg-gray-50'>
									<tr>
										<th
											className={cn(
												'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase',
												isMobile && 'w-1/3 p-1 align-text-top',
											)}>
											Cookie Type
										</th>
										<th
											className={cn(
												'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase',
												isMobile && 'w-1/3 p-1 align-text-top',
											)}>
											Administered By
										</th>
										<th
											className={cn(
												'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase',
												isMobile && 'w-1/3 p-1 align-text-top',
											)}>
											Purpose
										</th>
									</tr>
								</thead>
								<tbody className='bg-white'>
									<tr>
										<td
											className={cn(
												'px-4 py-3 text-sm text-gray-600',
												isMobile && 'p-0',
											)}>
											Necessary / Essential Cookies
											<br />
											(Session Cookies)
										</td>
										<td
											className={cn(
												'px-4 py-3 text-sm text-gray-600',
												isMobile && 'p-0 text-center',
											)}>
											Us
										</td>
										<td
											className={cn(
												'px-4 py-3 text-sm text-gray-600',
												isMobile && 'p-0',
											)}>
											These Cookies are essential to
											provide You with services available
											through the Website and to enable
											You to use some of its features.
										</td>
									</tr>
									<tr>
										<td
											className={cn(
												'px-4 py-3 text-sm text-gray-600',
												isMobile && 'p-0',
											)}>
											Cookies Policy / Notice Acceptance
											Cookies
											<br />
											(Persistent Cookies)
										</td>
										<td
											className={cn(
												'px-4 py-3 text-sm text-gray-600',
												isMobile && 'p-0 text-center',
											)}>
											Us
										</td>
										<td
											className={cn(
												'px-4 py-3 text-sm text-gray-600',
												isMobile && 'p-0',
											)}>
											These Cookies identify if users have
											accepted the use of cookies on the
											Website.
										</td>
									</tr>
									<tr>
										<td
											className={cn(
												'px-4 py-3 text-sm text-gray-600',
												isMobile && 'p-0',
											)}>
											Functionality Cookies
											<br />
											(Persistent Cookies)
										</td>
										<td
											className={cn(
												'px-4 py-3 text-sm text-gray-600',
												isMobile && 'p-0 text-center',
											)}>
											Us
										</td>
										<td
											className={cn(
												'px-4 py-3 text-sm text-gray-600',
												isMobile && 'p-0',
											)}>
											These Cookies allow us to remember
											choices You make when You use the
											Website, such as remembering your
											login details or language
											preference.
										</td>
									</tr>
								</tbody>
							</table>
						</div>

						<p className='text-gray-600'>
							For more information about the cookies we use,
							please visit our Cookies Policy.
						</p>
					</section>

					{/* Use of Personal Data */}
					<section
						id='use-of-data'
						className='mb-8'>
						<h2 className='text-2xl font-semibold text-gray-800 mb-4'>
							Use of Your Personal Data
						</h2>
						<p className='text-gray-600 mb-4'>
							The Company may use Personal Data for the following
							purposes:
						</p>

						<ul className='list-disc pl-5 space-y-2 text-gray-600 mb-4'>
							<li>
								To provide and maintain our Service, including
								to monitor the usage of our Service.
							</li>
							<li>
								To manage Your Account: to manage Your
								registration as a user of the Service.
							</li>
							<li>
								For the performance of a contract: the
								development, compliance, and undertaking of the
								purchase contract for the products, items, or
								services You have purchased.
							</li>
							<li>
								To contact You: To contact You by email,
								telephone calls, SMS, or other forms of
								electronic communication regarding updates or
								informative communications related to the
								functionalities, products, or contracted
								services.
							</li>
							<li>
								To provide You with news, special offers, and
								general information about other goods, services,
								and events which we offer.
							</li>
							<li>
								To manage Your requests: To attend and manage
								Your requests to Us.
							</li>
							<li>
								For business transfers: We may use Your
								information to evaluate or conduct a merger,
								divestiture, restructuring, reorganization,
								dissolution, or other sale or transfer of some
								or all of Our assets.
							</li>
							<li>
								For other purposes: We may use Your information
								for data analysis, identifying usage trends,
								determining the effectiveness of our promotional
								campaigns, and to evaluate and improve our
								Service.
							</li>
						</ul>

						<p className='text-gray-600 mb-2'>
							We may share Your personal information in the
							following situations:
						</p>

						<ul className='list-disc pl-5 space-y-2 text-gray-600'>
							<li>
								<strong>With Service Providers:</strong> We may
								share Your personal information with Service
								Providers to monitor and analyze the use of our
								Service.
							</li>
							<li>
								<strong>For business transfers:</strong> We may
								share or transfer Your personal information in
								connection with any merger, sale of Company
								assets, financing, or acquisition.
							</li>
							<li>
								<strong>With Affiliates:</strong> We may share
								Your information with Our affiliates, in which
								case we will require those affiliates to honor
								this Privacy Policy.
							</li>
							<li>
								<strong>With business partners:</strong> We may
								share Your information with Our business
								partners to offer You certain products,
								services, or promotions.
							</li>
							<li>
								<strong>With other users:</strong> When You
								share personal information or otherwise interact
								in public areas with other users, such
								information may be viewed by all users.
							</li>
							<li>
								<strong>With Your consent:</strong> We may
								disclose Your personal information for any other
								purpose with Your consent.
							</li>
						</ul>
					</section>

					{/* Retention of Personal Data */}
					<section
						id='retention-of-data'
						className='mb-8'>
						<h2 className='text-2xl font-semibold text-gray-800 mb-4'>
							Retention of Your Personal Data
						</h2>
						<p className='text-gray-600 mb-4'>
							The Company will retain Your Personal Data only for
							as long as is necessary for the purposes set out in
							this Privacy Policy. We will retain and use Your
							Personal Data to the extent necessary to comply with
							our legal obligations.
						</p>
						<p className='text-gray-600'>
							The Company will also retain Usage Data for internal
							analysis purposes. Usage Data is generally retained
							for a shorter period of time, except when this data
							is used to strengthen the security or to improve the
							functionality of Our Service.
						</p>
					</section>

					{/* Transfer of Personal Data */}
					<section
						id='transfer-of-data'
						className='mb-8'>
						<h2 className='text-2xl font-semibold text-gray-800 mb-4'>
							Transfer of Your Personal Data
						</h2>
						<p className='text-gray-600 mb-4'>
							Your information, including Personal Data, is
							processed at the Company's operating offices and in
							any other places where the parties involved in the
							processing are located.
						</p>
						<p className='text-gray-600 mb-4'>
							Your consent to this Privacy Policy followed by Your
							submission of such information represents Your
							agreement to that transfer.
						</p>
						<p className='text-gray-600'>
							The Company will take all steps reasonably necessary
							to ensure that Your data is treated securely and in
							accordance with this Privacy Policy.
						</p>
					</section>

					{/* Delete Personal Data */}
					<section
						id='delete-data'
						className='mb-8'>
						<h2 className='text-2xl font-semibold text-gray-800 mb-4'>
							Delete Your Personal Data
						</h2>
						<p className='text-gray-600 mb-4'>
							You have the right to delete or request that We
							assist in deleting the Personal Data that We have
							collected about You.
						</p>
						<p className='text-gray-600 mb-4'>
							Our Service may give You the ability to delete
							certain information about You from within the
							Service.
						</p>
						<p className='text-gray-600 mb-4'>
							You may update, amend, or delete Your information at
							any time by signing in to Your Account and visiting
							the account settings section. You may also contact
							Us to request access to, correct, or delete any
							personal information.
						</p>
						<p className='text-gray-600'>
							Please note, however, that We may need to retain
							certain information when we have a legal obligation
							or lawful basis to do so.
						</p>
					</section>

					{/* Disclosure of Personal Data */}
					<section
						id='disclosure-of-data'
						className='mb-8'>
						<h2 className='text-2xl font-semibold text-gray-800 mb-4'>
							Disclosure of Your Personal Data
						</h2>

						<h3 className='text-xl font-medium text-gray-700 mb-2'>
							Business Transactions
						</h3>
						<p className='text-gray-600 mb-4'>
							If the Company is involved in a merger, acquisition,
							or asset sale, Your Personal Data may be
							transferred. We will provide notice before Your
							Personal Data is transferred.
						</p>

						<h3 className='text-xl font-medium text-gray-700 mb-2'>
							Law Enforcement
						</h3>
						<p className='text-gray-600 mb-4'>
							Under certain circumstances, the Company may be
							required to disclose Your Personal Data if required
							to do so by law or in response to valid requests by
							public authorities.
						</p>

						<h3 className='text-xl font-medium text-gray-700 mb-2'>
							Other Legal Requirements
						</h3>
						<p className='text-gray-600'>
							The Company may disclose Your Personal Data in the
							good faith belief that such action is necessary to:
						</p>
						<ul className='list-disc pl-5 space-y-2 text-gray-600 mt-2'>
							<li>Comply with a legal obligation</li>
							<li>
								Protect and defend the rights or property of the
								Company
							</li>
							<li>
								Prevent or investigate possible wrongdoing in
								connection with the Service
							</li>
							<li>
								Protect the personal safety of Users of the
								Service or the public
							</li>
							<li>Protect against legal liability</li>
						</ul>
					</section>

					{/* Security of Personal Data */}
					<section
						id='security-of-data'
						className='mb-8'>
						<h2 className='text-2xl font-semibold text-gray-800 mb-4'>
							Security of Your Personal Data
						</h2>
						<p className='text-gray-600'>
							The security of Your Personal Data is important to
							Us, but remember that no method of transmission over
							the Internet or method of electronic storage is 100%
							secure.
						</p>
					</section>

					{/* Children's Privacy */}
					<section
						id='childrens-privacy'
						className='mb-8'>
						<h2 className='text-2xl font-semibold text-gray-800 mb-4'>
							Children's Privacy
						</h2>
						<p className='text-gray-600 mb-4'>
							Our Service does not address anyone under the age of
							13. We do not knowingly collect personally
							identifiable information from anyone under the age
							of 13. If You are a parent or guardian and You are
							aware that Your child has provided Us with Personal
							Data, please contact Us.
						</p>
						<p className='text-gray-600'>
							If We need to rely on consent as a legal basis for
							processing Your information and Your country
							requires consent from a parent, We may require Your
							parent's consent.
						</p>
					</section>

					{/* Links to Other Websites */}
					<section
						id='links-to-other-websites'
						className='mb-8'>
						<h2 className='text-2xl font-semibold text-gray-800 mb-4'>
							Links to Other Websites
						</h2>
						<p className='text-gray-600 mb-4'>
							Our Service may contain links to other websites that
							are not operated by Us. If You click on a third
							party link, You will be directed to that third
							party's site.
						</p>
						<p className='text-gray-600'>
							We have no control over and assume no responsibility
							for the content, privacy policies, or practices of
							any third party sites or services.
						</p>
					</section>

					{/* Changes to Privacy Policy */}
					<section
						id='changes-to-policy'
						className='mb-8'>
						<h2 className='text-2xl font-semibold text-gray-800 mb-4'>
							Changes to this Privacy Policy
						</h2>
						<p className='text-gray-600 mb-4'>
							We may update Our Privacy Policy from time to time.
							We will notify You of any changes by posting the new
							Privacy Policy on this page.
						</p>
						<p className='text-gray-600 mb-4'>
							We will let You know via email and/or a prominent
							notice on Our Service, prior to the change becoming
							effective.
						</p>
						<p className='text-gray-600'>
							You are advised to review this Privacy Policy
							periodically for any changes.
						</p>
					</section>

					{/* Contact Us */}
					<section
						id='contact-us'
						className='border-t border-gray-200 pt-6'>
						<h2 className='text-2xl font-semibold text-gray-800 mb-4'>
							Contact Us
						</h2>
						<p className='text-gray-600 mb-4'>
							If you have any questions about this Privacy Policy,
							You can contact us:
						</p>
						<ul className='list-disc pl-5 space-y-2 text-gray-600'>
							<li>
								By email:{' '}
								<a
									href='mailto:office@info@owninn.ge'
									className='text-blue-600 hover:underline'>
									info@owninn.ge
								</a>
							</li>
						</ul>
					</section>
				</div>
			</div>
		</div>
	);
};

export default PrivacyPolicy;
